import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { authApi } from "@/api/auth";
import { wechatApi } from "@/api/wechat";
import {
  readPersistedRefresh,
  setAccessToken,
  writePersistedRefresh,
} from "@/api/client";
import { isInMiniProgram, notifyLogout, notifyTokensUpdated } from "@/utils/mp-bridge";
import type { LoginIn, RegisterIn, UserOut, WechatCallbackIn } from "@/api/types";

/**
 * Auth store. Holds the access token in memory (never persisted) and
 * delegates refresh-token persistence to the api client (localStorage).
 *
 * Bootstrapping: on app mount, if a refresh token is in localStorage we
 * call ``bootstrap()`` which refreshes once and loads /users/me.
 */
export const useAuthStore = defineStore("auth", () => {
  const accessToken = ref<string | null>(null);
  const user = ref<UserOut | null>(null);
  const isBootstrapped = ref(false);

  const isAuthenticated = computed(() => accessToken.value !== null);

  function setTokens(access: string, refresh: string): void {
    accessToken.value = access;
    writePersistedRefresh(refresh);
    setAccessToken(access);
    // 嵌入小程序时，通知小程序同步 token（用于后续 web-view 重新加载）
    if (isInMiniProgram()) {
      notifyTokensUpdated(access, refresh);
    }
  }

  function clearState(): void {
    accessToken.value = null;
    user.value = null;
    setAccessToken(null);
    writePersistedRefresh(null);
    // 嵌入小程序时，通知小程序清除登录态并回到启动页
    if (isInMiniProgram()) {
      notifyLogout();
    }
  }

  async function login(payload: LoginIn): Promise<void> {
    const result = await authApi.login(payload);
    setTokens(result.tokens.access_token, result.tokens.refresh_token);
    user.value = result.user;
  }

  async function register(payload: RegisterIn): Promise<void> {
    const result = await authApi.register(payload);
    setTokens(result.tokens.access_token, result.tokens.refresh_token);
    user.value = result.user;
  }

  /** Redirect to WeChat OAuth2 authorize page. */
  async function loginWithWechat(): Promise<void> {
    const { authorize_url } = await wechatApi.authorize();
    window.location.href = authorize_url;
  }

  /** Complete WeChat OAuth callback: exchange code for JWT. */
  async function wechatCallback(payload: WechatCallbackIn): Promise<void> {
    const result = await wechatApi.callback(payload);
    setTokens(result.tokens.access_token, result.tokens.refresh_token);
    user.value = result.user;
  }

  async function logout(): Promise<void> {
    const refresh = readPersistedRefresh();
    try {
      if (refresh) await authApi.logout({ refresh_token: refresh });
    } catch {
      // best-effort: the server may already have invalidated it
    }
    clearState();
  }

  /** Best-effort bootstrap: refresh once if a refresh token exists, then load /users/me. */
  async function bootstrap(): Promise<void> {
    if (isBootstrapped.value) return;
    isBootstrapped.value = true;
    const refresh = readPersistedRefresh();
    if (!refresh) return;
    try {
      const tokens = await authApi.refresh({ refresh_token: refresh });
      setTokens(tokens.access_token, tokens.refresh_token);
      user.value = await authApi.me();
    } catch {
      clearState();
    }
  }

  return {
    accessToken,
    user,
    isAuthenticated,
    isBootstrapped,
    login,
    register,
    loginWithWechat,
    wechatCallback,
    logout,
    bootstrap,
  };
});
